import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../../services/authService';
import { useAuthStore } from '../../../stores/authStore';
import { showToast } from '../../../stores/uiStore';
import { handleApiError } from '../../../services/api';
import { ROUTES } from '../../../config/routes';
import { getOperationErrorMessage } from '../../../config/errorMessages';

export const useUpdateProfile = () => {
  const { updateUser } = useAuthStore();

  return useMutation({
    mutationFn: (data: { name?: string; email_notifications?: boolean; weekly_report_enabled?: boolean }) =>
      authService.updateProfile(data),
    onSuccess: (response) => {
      if (response.success && response.data) {
        updateUser(response.data);
        showToast.success('プロフィールを更新しました');
      } else {
        showToast.error(response.error?.message || getOperationErrorMessage('update'));
      }
    },
    onError: (error) => {
      const apiError = handleApiError(error);
      showToast.error(apiError.message);
    },
  });
};

export const useChangePassword = () => {
  return useMutation({
    mutationFn: ({ currentPassword, newPassword }: { currentPassword: string; newPassword: string }) =>
      authService.changePassword(currentPassword, newPassword),
    onSuccess: (response) => {
      if (response.success) {
        showToast.success('パスワードを変更しました');
      } else {
        showToast.error(response.error?.message || getOperationErrorMessage('auth'));
      }
    },
    onError: (error) => {
      const apiError = handleApiError(error);
      showToast.error(apiError.message);
    },
  });
};

export const useChangeEmail = () => {
  return useMutation({
    mutationFn: ({ password, newEmail }: { password: string; newEmail: string }) =>
      authService.changeEmail(password, newEmail),
    onSuccess: (response) => {
      if (!response.success) {
        showToast.error(response.error?.message || getOperationErrorMessage('submit'));
      }
    },
    onError: (error) => {
      const apiError = handleApiError(error);
      showToast.error(apiError.message);
    },
  });
};

export const useVerifyEmail = () => {
  const { updateUser } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (code: string) => authService.verifyEmail(code),
    onSuccess: (response) => {
      if (response.success && response.data) {
        if (response.data.user) {
          updateUser(response.data.user);
        }
        queryClient.invalidateQueries({ queryKey: ['profile'] });
        showToast.success('メールアドレスを変更しました');
      } else {
        showToast.error(response.error?.message || getOperationErrorMessage('submit'));
      }
    },
    onError: (error) => {
      const apiError = handleApiError(error);
      showToast.error(apiError.message);
    },
  });
};

export const useDeleteAccount = () => {
  const navigate = useNavigate();
  const { clearAuth } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vars: { password: string; reason_category?: string; reason_detail?: string }) =>
      authService.deleteAccount(vars.password, vars.reason_category, vars.reason_detail),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.clear();
        clearAuth();
        showToast.success('アカウントを削除しました');
        navigate(ROUTES.LOGIN);
      } else {
        showToast.error(response.error?.message || getOperationErrorMessage('submit'));
      }
    },
    onError: (error) => {
      const apiError = handleApiError(error);
      showToast.error(apiError.message);
    },
  });
};
